import React, { useState, useEffect, useCallback } from "react";
import IslandHeader from "components/IslandHeader";
import RoleSectionContainer from "./RoleSectionContainer";
import RoleMembersPanel from "./RoleMembersPanel";
import roleOperations from "./duck/operations";
import { Mixpanel } from "helpers/Mixpanel";
import "./RoleSection.scss";

export function RoleComponent({ locationId, dispatch, roles }) {
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [assigningUserId, setAssigningUserId] = useState(null);

  const fetchMembers = useCallback(() => {
    if (!locationId) return;
    setMembersLoading(true);
    dispatch(roleOperations.fetchRolePageMembersOperation(locationId))
      .then((result) => {
        setMembers(result || []);
        setMembersLoading(false);
      });
  }, [locationId, dispatch]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Reset selection when location changes
  useEffect(() => {
    setSelectedRoleId(null);
    setMembers([]);
  }, [locationId]);

  // Auto-select first role when roles load and nothing is selected
  useEffect(() => {
    if (!selectedRoleId && roles && roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  // Clear selection if the selected role was deleted
  useEffect(() => {
    if (selectedRoleId && roles && roles.length > 0) {
      const stillExists = roles.some((r) => r.id === selectedRoleId);
      if (!stillExists) {
        setSelectedRoleId(roles[0]?.id || null);
      }
    }
  }, [roles, selectedRoleId]);

  const handleSelectRole = (roleId) => {
    setSelectedRoleId((prev) => (prev === roleId ? null : roleId));
  };

  const handleAddRoleToUser = (userId, existingTagIds) => {
    if (!selectedRoleId || assigningUserId) return;
    Mixpanel.track('Add member to role', { role_id: selectedRoleId, user_id: userId });
    setAssigningUserId(userId);
    dispatch(roleOperations.addRoleToUserOperation(userId, selectedRoleId, existingTagIds, locationId))
      .then((updatedMembers) => {
        if (updatedMembers) {
          setMembers(updatedMembers);
        }
        setAssigningUserId(null);
      });
  };

  const handleRemoveRoleFromUser = (userId, existingTagIds) => {
    if (!selectedRoleId || assigningUserId) return;
    Mixpanel.track('Remove member from role', { role_id: selectedRoleId, user_id: userId });
    setAssigningUserId(userId);
    dispatch(roleOperations.removeRoleFromUserOperation(userId, selectedRoleId, existingTagIds, locationId))
      .then((updatedMembers) => {
        if (updatedMembers) {
          setMembers(updatedMembers);
        }
        setAssigningUserId(null);
      });
  };

  return (
    <div className="island-container">
      <div className="island-content">
        <IslandHeader title="Manage Roles" />
        <div className="role-page-split">
          <div className="role-page-left">
            <RoleSectionContainer
              selectedRoleId={selectedRoleId}
              onSelectRole={handleSelectRole}
              members={members}
            />
          </div>
          {selectedRoleId && (
            <div className="role-page-right">
              <RoleMembersPanel
                selectedRoleId={selectedRoleId}
                members={members}
                membersLoading={membersLoading}
                assigningUserId={assigningUserId}
                onAddRoleToUser={handleAddRoleToUser}
                onRemoveRoleFromUser={handleRemoveRoleFromUser}
                roles={roles}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoleComponent;

